import React, { useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    borders, colors, space, radii, mediaQueries
} from 'style/config';
import Empty from 'constants/empty';
import { CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import recServiceUtils from 'utils/RecommendationService';
import actions from 'Actions';
import snbApi from 'services/api/search-n-browse';
import BundleHeader from 'components/ProductPage/PdpBundling/BundleWidget/BundleHeader';
import BundleProductCard from 'components/ProductPage/PdpBundling/BundleWidget/BundleProductCard';
import PriceTotal from 'components/ProductPage/PdpBundling/BundleWidget/PriceTotal';
import AddAllButton from 'components/ProductPage/PdpBundling/BundleWidget/AddAllButton';
import useBundleRecommendations from 'components/ProductPage/PdpBundling/BundleWidget/hooks/useBundleRecommendations';
import { BUNDLE_DATA_SOURCE, resolveBundleExperience } from 'components/ProductPage/PdpBundling/BundleWidget/utils/resolveBundleExperience';
import {
    buildBundleSwapOpenProducts,
    buildBundleSwapConfirmedProducts
} from 'components/ProductPage/PdpBundling/BundleWidget/utils/buildBundleAtbProducts';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import localeUtils from 'utils/LanguageLocale';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const CAROUSEL_CARD_WIDTH = 210;
const { getSeedItems } = recServiceUtils;

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const BundleWidget = ({ currentProduct }) => {
    // TODO FER-790: field names will change when BE completes FER-586.
    const {
        productBundleDataSource, useItWithTitle, ancillarySkus = Empty.Array, currentSku, productDetails
    } = currentProduct || Empty.Object;

    const { dataSource: normalizedBundleDataSource, title: resolvedTitle } = resolveBundleExperience({
        productBundleDataSource,
        useItWithTitle
    });
    const isAutomated = normalizedBundleDataSource === BUNDLE_DATA_SOURCE.Automated;

    const {
        bundleProducts: automatedBundleProducts,
        resultId,
        totalResults
    } = useBundleRecommendations({
        isAutomated,
        productId: productDetails?.productId
    });

    const bundleProducts = useMemo(() => {
        if (isAutomated) {
            return automatedBundleProducts;
        }

        if (normalizedBundleDataSource === BUNDLE_DATA_SOURCE.Manual) {
            return ancillarySkus.filter(sku => !sku.isOutOfStock);
        }

        return Empty.Array;
    }, [isAutomated, automatedBundleProducts, normalizedBundleDataSource, ancillarySkus]);

    // FER-889: the hero SKU's displayName is "<skuId> <variationValue>", so surface the product name instead.
    const heroProduct = useMemo(() => {
        const sku = currentSku || Empty.Object;

        return {
            ...sku,
            productName: sku.productName || productDetails?.displayName
        };
    }, [currentSku, productDetails]);

    const isCarousel = 1 + bundleProducts.length > 4;
    const dispatch = useDispatch();
    const [deselectedSkuIds, setDeselectedSkuIds] = useState(new Set());
    const [selectedSkus, setSelectedSkus] = useState({});
    const isLoading = useRef(false);

    const bundleTitle = resolvedTitle || getText('frequentlyBoughtTogether');
    const bundleType = isAutomated ? BUNDLE_DATA_SOURCE.Automated : BUNDLE_DATA_SOURCE.Manual;

    const handleChangeSkuClick = async sku => {
        if (isLoading.current) {
            return;
        }

        isLoading.current = true;

        const product = await snbApi.getProductDetailsLite(sku.productId, sku.skuId).catch(() => undefined);
        isLoading.current = false;

        // SKU currently occupying this bundle slot — a previous swap wins over the API/base sku.
        const skuInSlot = selectedSkus[sku.skuId] ?? product?.currentSku ?? sku;

        // Fired before the dispatch so the tracked SKU is the one the modal is about to show.
        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
            data: {
                pageName: `${anaConsts.PAGE_TYPES.BUNDLE_CHOOSE_OPTIONS_MODAL}:n/a:*`,
                pageType: anaConsts.PAGE_TYPES.BUNDLE_CHOOSE_OPTIONS_MODAL,
                world: 'n/a',
                previousPageName: anaUtils.getLastAsyncPageLoadData().pageName,
                productStrings: buildBundleSwapOpenProducts({
                    skuId: skuInSlot?.skuId,
                    bundleName: bundleTitle,
                    bundleType
                })
            }
        });

        const swapCallback = newCurrentSku => {
            // Only an actual swap counts, so confirming the same SKU does not inflate the swap rate.
            if (newCurrentSku?.skuId && newCurrentSku.skuId !== skuInSlot?.skuId) {
                processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                    data: {
                        actionInfo: anaConsts.EVENT_NAMES.PRODUCT_PAGE.PDP_BUNDLE_SWAP_CONFIRMED,
                        linkName: anaConsts.EVENT_NAMES.PRODUCT_PAGE.PDP_BUNDLE_SWAP_CONFIRMED,
                        productStrings: buildBundleSwapConfirmedProducts({
                            newSkuId: newCurrentSku.skuId,
                            originalSkuId: skuInSlot?.skuId,
                            bundleName: bundleTitle,
                            bundleType
                        })
                    }
                });
            }

            setSelectedSkus(prev => ({ ...prev, [sku.skuId]: newCurrentSku }));
        };

        dispatch(
            actions.showBundleChangeSkuModal({
                isOpen: true,
                skuToEdit: {
                    ...sku,
                    variationType: product?.variationType ?? sku.variationType,
                    skuSelectorType: product?.skuSelectorType ?? sku.skuSelectorType,
                    currentSku: skuInSlot,
                    regularChildSkus: [...(product?.regularChildSkus ?? Empty.Array), ...(product?.onSaleChildSkus ?? Empty.Array)]
                },
                bundleSkuSwapCallback: swapCallback
            })
        );
    };

    if (!bundleProducts.length) {
        return null;
    }

    const handleCheckboxChange = skuId => {
        setDeselectedSkuIds(prev => {
            const next = new Set(prev);
            next.has(skuId) ? next.delete(skuId) : next.add(skuId);

            return next;
        });
    };

    const allBundleProducts = bundleProducts.map(sku => (selectedSkus[sku.skuId] ? { ...sku, ...selectedSkus[sku.skuId] } : sku));
    const selectedBundleProducts = allBundleProducts.filter(sku => !deselectedSkuIds.has(sku.skuId));
    const hasSelection = selectedBundleProducts.length > 0;

    const seedItems = getSeedItems(CONSTRUCTOR_PODS.PDP_BUNDLING, { itemIds: productDetails?.productId });
    const constructorContainerAttributes = isAutomated
        ? {
            'data-cnstrc-recommendations': true,
            'data-cnstrc-recommendations-pod-id': CONSTRUCTOR_PODS.PDP_BUNDLING,
            'data-cnstrc-result-id': resultId,
            'data-cnstrc-num-results': totalResults,
            ...(seedItems && { 'data-cnstrc-recommendations-seed-items': seedItems })
        }
        : Empty.Object;

    return (
        <section css={styles.container}>
            <BundleHeader title={bundleTitle} />
            <div css={styles.widgetCard}>
                <ul
                    css={[styles.productsWrapper, isCarousel && styles.productsWrapperCarousel]}
                    aria-label={getText('bundleProductOptions')}
                    {...constructorContainerAttributes}
                >
                    <li>
                        <BundleProductCard
                            product={heroProduct}
                            isCurrentProduct
                        />
                    </li>
                    {bundleProducts.map(sku => {
                        const displayProduct = selectedSkus[sku.skuId] ? { ...sku, ...selectedSkus[sku.skuId] } : sku;

                        return (
                            <li key={sku.skuId}>
                                <BundleProductCard
                                    product={displayProduct}
                                    isChecked={!deselectedSkuIds.has(sku.skuId)}
                                    onCheckboxChange={() => handleCheckboxChange(sku.skuId)}
                                    onChangeSkuClick={() => handleChangeSkuClick(sku)}
                                />
                            </li>
                        );
                    })}
                </ul>
                <div css={styles.footer}>
                    <PriceTotal
                        currentProduct={currentSku}
                        bundleProducts={selectedBundleProducts}
                    />
                    <AddAllButton
                        disabled={!hasSelection}
                        selectedItems={selectedBundleProducts}
                        currentProduct={currentProduct}
                        allBundleProducts={allBundleProducts}
                        deselectedSkuIds={deselectedSkuIds}
                        bundleName={bundleTitle}
                        bundleType={bundleType}
                        isConstructorRecommendations={isAutomated}
                    />
                </div>
            </div>
        </section>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: space[4],
        marginTop: space[4],
        [mediaQueries.sm]: {
            marginTop: space[5]
        }
    },
    widgetCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[3],
        width: '100%',
        border: `${borders[1]} ${colors.lightGray}`,
        borderRadius: radii[3],
        overflow: 'hidden',
        paddingBottom: space[4],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            padding: space[5],
            gap: 0
        }
    },
    productsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        listStyle: 'none',
        padding: 0,
        margin: 0,
        [mediaQueries.sm]: {
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            overflowX: 'auto',
            gap: space[5],
            paddingBottom: space[4],
            '& > li': {
                flex: 1,
                boxSizing: 'border-box'
            },
            '& > li:not(:first-of-type)': {
                borderLeft: `${borders[1]} ${colors.lightGray}`,
                paddingLeft: space[5]
            }
        }
    },
    productsWrapperCarousel: {
        [mediaQueries.sm]: {
            '& > li': {
                flex: 'none',
                flexShrink: 0,
                width: CAROUSEL_CARD_WIDTH
            }
        }
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4],
        padding: `0 ${space[4]}px`,
        width: '100%',
        [mediaQueries.sm]: {
            padding: 0,
            paddingLeft: space[6],
            width: 'auto',
            justifyContent: 'center',
            borderLeft: `${borders[1]} ${colors.lightGray}`
        }
    }
};

export default wrapFunctionalComponent(BundleWidget, 'BundleWidget');
